/**
 * Native `<select>` primitive styled to match the shadcn theme (#42).
 *
 * We deliberately avoid the Radix-based shadcn Select for the Sprint-1
 * closure: it ships a separate listbox, requires a portal, and inflates the
 * test setup considerably. The native control is keyboard-accessible out of
 * the box, plays well with form-state, and renders the same on every
 * platform - good enough for filter dropdowns.
 *
 * If we later need typeahead / async-loaded options (e.g. customer-combobox
 * for big tenants), we replace this with the Radix variant; consumers do
 * not change because the props mirror an `HTMLSelectElement`.
 */
import { type SelectHTMLAttributes, forwardRef } from 'react';

import { cn } from '@/lib/utils';

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'inline-flex h-10 w-full appearance-none items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
